(() => {
  if (customElements.get('rr-compass-personalization')) return;
  class RRCompassPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      try { this.config = JSON.parse(this.querySelector('[data-compass-config]').textContent); } catch (_) { return; }
      this.started = true;
      this.abort = new AbortController();
      this.form = this.closest('form[action*="/cart/add"]');
      this.scope = this.closest('product-info') || this.closest('section');
      this.dialog = this.querySelector('[data-compass-dialog]');
      this.panel = this.querySelector('.rr-compass__panel');
      this.letter = this.querySelector('[data-compass-letter]');
      this.sign = this.querySelector('[data-compass-sign]');
      this.placeholder = this.querySelector('[data-compass-placeholder]');
      this.caption = this.querySelector('[data-compass-caption]');
      this.addButton = this.querySelector('[data-compass-add]');
      this.message = this.querySelector('[data-compass-message]');
      this.loaded = new Map(); this.artReady = false; this.artRequest = 0;
      const on = (target, event, handler, extra = {}) => target?.addEventListener(event, handler, { signal: this.abort.signal, ...extra });
      on(this.querySelector('[data-compass-open]'), 'click', () => this.open());
      on(this.querySelector('[data-compass-close]'), 'click', () => this.close());
      on(this.dialog, 'click', event => { if (event.target === this.dialog) this.close(); });
      on(this.dialog, 'keydown', event => this.handleKey(event));
      on(this.letter, 'change', () => this.updateLetter());
      this.querySelectorAll('[data-compass-finish]').forEach(button => on(button, 'click', () => this.chooseFinish(button.dataset.compassFinish)));
      on(this.scope, 'change', () => this.sync());
      on(this.scope, 'input', () => this.sync());
      on(this.form, 'submit', event => {
        this.sync();
        if (!this.valid) { event.preventDefault(); event.stopImmediatePropagation(); this.open(); }
      }, { capture: true });
      on(this.addButton, 'click', () => {
        this.sync();
        if (!this.valid) return;
        const submit = this.productSubmit;
        if (!submit || !this.form) return;
        if (!this.form.checkValidity()) { this.form.reportValidity(); return; }
        this.form.requestSubmit(submit);
        this.close();
      });
      this.observer = new MutationObserver(() => this.sync());
      if (this.scope) this.observer.observe(this.scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled', 'aria-disabled'] });
      this.sync(); this.updateLetter();
    }
    get productSubmit() { return this.form?.querySelector('[type="submit"][name="add"]'); }
    controls(name) {
      return [...(this.scope?.querySelectorAll('variant-selects input[type="radio"],variant-radios input[type="radio"],variant-selects select,variant-radios select') || [])]
        .filter(control => control.tagName === 'SELECT' ? control.name === `options[${name}]` : control.dataset.optionName === name);
    }
    selected(name) { return this.controls(name).find(control => control.tagName === 'SELECT' || control.checked)?.value || ''; }
    chooseFinish(finish) {
      const control = this.controls('Color').find(item => item.tagName === 'SELECT' ? [...item.options].some(option => option.value === finish && !option.disabled) : item.value === finish && !item.disabled);
      if (!control) return;
      if (control.tagName === 'SELECT') control.value = finish; else control.checked = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
      control.dispatchEvent(new Event('input', { bubbles: true }));
      this.sync();
    }
    sync() {
      if (!this.started) return;
      const options = this.config.options.map(name => this.selected(name));
      const finish = this.selected('Color');
      const size = this.selected('Size');
      if (this.dataset.selectedFinish !== finish) this.dataset.selectedFinish = finish;
      this.expectedVariant = this.config.variants.find(variant => variant.options.every((value, index) => value === options[index]));
      this.querySelectorAll('[data-compass-finish]').forEach(button => {
        const pressed = String(button.dataset.compassFinish === finish);
        if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      });
      const label = this.letter.value;
      const caption = [label ? `Initial ${label}` : '', size ? `${size} inches wide` : '', finish].filter(Boolean).join(' · ');
      if (this.caption.textContent !== caption) this.caption.textContent = caption;
      this.querySelector('.rr-compass__stage').setAttribute('aria-label', label ? `Compass monogram ${label} in ${finish || 'your selected finish'}` : 'Choose an initial to preview your compass sign');
      const nativeId = this.form?.querySelector('input[name="id"]');
      const submit = this.productSubmit;
      const resolved = !!this.expectedVariant && String(this.expectedVariant.id) === nativeId?.value;
      const available = resolved && this.expectedVariant.available && submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true' && !nativeId.disabled;
      this.valid = this.config.letters.includes(label) && this.artReady && !!finish && !!available;
      const disabled = !this.valid;
      if (this.addButton.disabled !== disabled) this.addButton.disabled = disabled;
      const message = !label ? 'Choose your initial to continue.' : !this.artReady ? (this.artFailed ? 'The preview could not load. Choose your letter again to retry.' : 'Loading your exact letter design…') : !resolved ? 'Updating your size and finish…' : !available ? 'This selection is currently unavailable.' : 'Ready to add to cart.';
      if (this.message.textContent !== message) this.message.textContent = message;
    }
    async updateLetter() {
      const letter = this.letter.value;
      const url = this.config.art[letter];
      const request = ++this.artRequest;
      this.artReady = false; this.artFailed = false;
      this.sign.hidden = true;
      this.placeholder.hidden = false;
      this.placeholder.textContent = url ? 'Loading your letter…' : 'Choose your letter to see the design';
      this.sync();
      if (!url || !this.config.letters.includes(letter)) return;
      if (!this.loaded.has(url)) this.loaded.set(url, new Promise(resolve => {
        const image = new Image(); image.onload = () => resolve(true); image.onerror = () => resolve(false); image.src = url;
      }));
      const success = await this.loaded.get(url);
      if (!this.isConnected || request !== this.artRequest) return;
      if (!success) { this.loaded.delete(url); this.artFailed = true; this.placeholder.textContent = 'Preview unavailable. Choose your letter again to retry.'; this.sync(); return; }
      this.sign.style.setProperty('--rr-compass-mask', `url("${url}")`);
      this.artReady = true; this.sign.hidden = false; this.placeholder.hidden = true;
      this.sync();
      // The shared photographed-material adapter repaints the original shape.
      this.dispatchEvent(new Event('input', { bubbles: true }));
    }
    open() {
      this.returnFocus = document.activeElement;
      this.dialog.hidden = false; document.documentElement.classList.add('rr-compass-open');
      if (!this.materialsMounted) {
        const material = this.querySelector('[data-compass-photo-template]')?.content.querySelector('rr-photo-finish-materials');
        if (material) { this.materialsMounted = true; this.append(material.cloneNode(true)); }
      }
      this.sync();
      requestAnimationFrame(() => this.letter.focus());
    }
    close() {
      this.dialog.hidden = true; document.documentElement.classList.remove('rr-compass-open');
      (this.returnFocus?.isConnected ? this.returnFocus : this.querySelector('[data-compass-open]'))?.focus();
    }
    handleKey(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('button:not([disabled]),select:not([disabled]),[tabindex="0"]')].filter(element => element.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.artRequest++;
      if (!this.dialog?.hidden) document.documentElement.classList.remove('rr-compass-open');
      this.started = false;
    }
  }
  customElements.define('rr-compass-personalization', RRCompassPersonalization);
})();
